import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Navigation, Plus, Trash2, Save, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const NavigationManager = () => {
  const [siteConfig, setSiteConfig] = useState({});
  const [navLinks, setNavLinks] = useState([]);
  const [footerLinks, setFooterLinks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/site-config`);
      const cfg = res.data || {};
      setSiteConfig(cfg);
      try { setNavLinks(JSON.parse(cfg.navbar_links || "[]")); } catch { setNavLinks([]); }
      try { setFooterLinks(JSON.parse(cfg.footer_links || "[]")); } catch { setFooterLinks([]); }
    } catch {
      toast.error("Failed to load navigation config");
    } finally {
      setLoading(false);
    }
  };

  const saveNavigation = async () => {
    setSaving(true);
    try {
      const payload = {
        navbar_links: JSON.stringify(navLinks),
        footer_links: JSON.stringify(footerLinks),
        logo_url: siteConfig.logo_url || "",
        footer_copyright: siteConfig.footer_copyright || "",
      };
      await axios.put(`${API}/site-config`, payload, { withCredentials: true });
      toast.success("Navigation updated successfully!");
    } catch {
      toast.error("Failed to update navigation");
    } finally {
      setSaving(false);
    }
  };

  const addLink = (setLinks, links) => setLinks([...links, { label: "New Link", url: "/" }]);
  const updateLink = (setLinks, links, index, key, value) => {
    const updated = [...links];
    updated[index][key] = value;
    setLinks(updated);
  };
  const removeLink = (setLinks, links, index) => setLinks(links.filter((_, i) => i !== index));

  const renderLinkEditor = (title, links, setLinks) => (
    <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06]">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-gray-900 dark:text-white font-outfit text-base">{title}</CardTitle>
        <Button size="sm" variant="outline" onClick={() => addLink(setLinks, links)} className="text-xs bg-gray-100 dark:bg-white/5 border-gray-200 dark:border-white/10 hover:bg-white/10 text-gray-900 dark:text-white">
          <Plus className="w-3 h-3 mr-1" /> Add Link
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {links.length === 0 && <p className="text-xs text-zinc-500 italic">No links added.</p>}
        {links.map((link, i) => (
          <div key={i} className="flex gap-2 items-center bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] p-2 rounded border border-gray-100 dark:border-white/[0.04]">
            <div className="grid grid-cols-2 gap-2 flex-1">
              <div>
                <Label className="text-[10px] text-zinc-500">Label</Label>
                <Input value={link.label} onChange={e => updateLink(setLinks, links, i, "label", e.target.value)} className="h-8 text-xs bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/10 text-gray-900 dark:text-white" />
              </div>
              <div>
                <Label className="text-[10px] text-zinc-500">URL / Path</Label>
                <Input value={link.url} onChange={e => updateLink(setLinks, links, i, "url", e.target.value)} className="h-8 text-xs bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/10 text-gray-900 dark:text-white font-mono" />
              </div>
            </div>
            <Button size="sm" variant="destructive" onClick={() => removeLink(setLinks, links, i)} className="h-8 w-8 p-0 mt-4 bg-red-500/20 hover:bg-red-500/40 text-red-500 shrink-0">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );

  if (loading) return <div className="py-12 text-center"><RefreshCw className="w-8 h-8 text-emerald-600 dark:text-emerald-400 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-gray-900 dark:text-white font-outfit font-bold flex items-center gap-2"><Navigation className="w-5 h-5 text-emerald-600 dark:text-emerald-400" /> Navigation Manager</h2>
        <Button onClick={saveNavigation} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-xs rounded-xl transition-all px-4 h-9">
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Navigation
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          {renderLinkEditor("Navbar Links", navLinks, setNavLinks)}
          
          <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06]">
            <CardHeader><CardTitle className="text-gray-900 dark:text-white font-outfit text-base">Branding</CardTitle></CardHeader>
            <CardContent>
              <Label className="text-xs text-zinc-400">Custom Logo URL (optional)</Label>
              <Input 
                value={siteConfig.logo_url || ""} 
                onChange={e => setSiteConfig(s => ({...s, logo_url: e.target.value}))} 
                className="mt-1 bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/10 text-gray-900 dark:text-white" 
                placeholder="https://.../logo.png" 
              />
              <p className="text-[10px] text-zinc-500 mt-2">Leave blank to use default Jamadhan logo.</p>
            </CardContent>
          </Card>
        </div>
        
        <div className="space-y-6">
          {renderLinkEditor("Footer Links", footerLinks, setFooterLinks)}
          
          <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06]">
            <CardHeader><CardTitle className="text-gray-900 dark:text-white font-outfit text-base">Footer Global Details</CardTitle></CardHeader>
            <CardContent>
              <Label className="text-xs text-zinc-400">Footer Copyright Text</Label>
              <Input 
                value={siteConfig.footer_copyright || ""} 
                onChange={e => setSiteConfig(s => ({...s, footer_copyright: e.target.value}))} 
                className="mt-1 bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/10 text-gray-900 dark:text-white" 
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default NavigationManager;
